import {
  createFileRoute,
  useParams,
  Outlet,
  useLocation,
  useRouter,
} from "@tanstack/react-router"
import {
  LayoutWithSidebar,
  DetailSidebar,
  useLayoutConfig,
  ProfileCard,
  Separator,
  DetailInfoList,
  formatLearningPathName,
  CollapsibleWithTitle,
  EmptyState,
  studentTypeMap,
  formatBool,
  formatDateToDutch,
  Badge,
  getStudentStatusLabel,
  studentStatusToBadgeVariant,
  InterruptionCard,
} from "toptaal-shared"
import ScreenWrapper from "@/components/ScreenWrapper"
import { checkOrganizationPermissions } from "@/utils/checkOrganizationPermissions"
import { useStudentSuspenseQuery } from "@/api/queries/students/useStudentSuspenseQuery"
import { useStudentCustomerManagersSuspenseQuery } from "@/api/queries/students/useStudentCustomerManagersSuspenseQuery"
import { useStudentCurrentInterruptionQuery } from "@/api/queries/students/useStudentCurrentInterruptionQuery"

export const Route = createFileRoute("/beheer/cursisten/$studentId")({
  component: Parent,
  beforeLoad: () => checkOrganizationPermissions("read", "students"),
})

function Parent() {
  const { pathname } = useLocation()
  const layoutConfig = useLayoutConfig()

  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId",
  })

  const base = `/beheer/cursisten/${studentId}`

  const sidebarNavigationItems = [
    {
      label: "Gegevens",
      to: `${base}/gegevens`,
    },
    {
      label: "Intake",
      to: `${base}/intake`,
    },
    {
      label: "2e aanspreekpunt",
      to: `${base}/tweede-aanspreekpunt`,
    },
    {
      label: "Voortgang",
      to: `${base}/voortgang`,
    },
    {
      label: "Rapportages",
      to: `${base}/rapportages`,
    },
    {
      label: "Correspondentie",
      to: `${base}/correspondentie`,
    },
    {
      label: "Lessen",
      to: `${base}/lessen`,
    },
  ]

  return (
    <ScreenWrapper className="!px-0">
      <LayoutWithSidebar
        rightPanelClassName={layoutConfig.rightPanelClassName}
        disableRightPanelSuspense={layoutConfig.disableRightPanelSuspense}
        sidebar={
          <DetailSidebar
            items={sidebarNavigationItems}
            isItemActive={(route: string) => pathname.startsWith(route)}
            goBackLabel="Cursist"
            goBackRoute="/beheer/cursisten"
          >
            <StudentDetails />
          </DetailSidebar>
        }
      >
        <Outlet />
      </LayoutWithSidebar>
    </ScreenWrapper>
  )
}

function StudentDetails() {
  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId",
  })
  const {
    data: { data: student },
  } = useStudentSuspenseQuery(Number(studentId))

  const {
    data: { customerManagers },
  } = useStudentCustomerManagersSuspenseQuery(Number(studentId))

  const { data: currentInterruption } = useStudentCurrentInterruptionQuery(
    student.currentStudentProjectOrganization?.id,
  )

  return (
    <>
      {currentInterruption && (
        <InterruptionCard interruption={currentInterruption} />
      )}

      <ProfileCard
        className="mb-2 mt-6"
        firstName={student.firstName}
        lastName={student.lastName}
        email={student.email}
        phoneNumber={student.phoneNumberCall}
      >
        <Separator className="my-4" />

        <DetailInfoList
          items={[
            {
              label: "Contract",
              value: student.currentStudentProjectOrganization?.project.name,
            },
            {
              label: "Leerlijn",
              value: formatLearningPathName({
                learningPath: student.currentLearningPath,
                moduleCode: student.currentModule?.moduleCode,
              }),
            },
            {
              label: "App",
              value: formatBool(student.appInstalled),
            },
            {
              label: "Status",
              value: (
                <Badge
                  variant={studentStatusToBadgeVariant(
                    student.currentStudentProjectOrganization?.status,
                  )}
                >
                  {getStudentStatusLabel(
                    student.currentStudentProjectOrganization?.status,
                  )}
                </Badge>
              ),
            },
            {
              label: "Aanmelddatum",
              value: formatDateToDutch(
                student.currentStudentProjectOrganization?.registeredAt,
                { format: "date" },
              ),
            },
            ...(student.currentStudentProjectOrganization?.studentType != null
              ? [
                  {
                    label: "Student type",
                    value:
                      studentTypeMap[
                        student.currentStudentProjectOrganization.studentType
                      ],
                  },
                ]
              : []),
          ]}
        />
      </ProfileCard>

      <Separator className="my-4" />
      <CollapsibleWithTitle
        title="Medewerkers"
        isOpenInitial={false}
        canOpen={customerManagers.length > 0}
      >
        <StudentCustomerManagers />
      </CollapsibleWithTitle>
      <Separator className="my-6" />
    </>
  )
}

function StudentCustomerManagers() {
  const router = useRouter()
  const { studentId } = useParams({
    from: "/beheer/cursisten/$studentId",
  })

  const {
    data: { customerManagers },
  } = useStudentCustomerManagersSuspenseQuery(Number(studentId))

  if (customerManagers.length === 0) {
    return <EmptyState>Geen medewerkers gevonden</EmptyState>
  }

  const handleNavigateToCustomerManager = (organizationUserId: number) => {
    router.navigate({
      to: "/beheer/medewerkers/$organizationUserId/gegevens",
      params: { organizationUserId: String(organizationUserId) },
      search: { from: router.state.location.href },
    })
  }

  return (
    <>
      {customerManagers.map((manager) => (
        <ProfileCard
          key={manager.id}
          firstName={manager.firstName}
          lastName={manager.lastName}
          email={manager.email}
          phoneNumber={manager.phoneNumber}
          onClick={() => handleNavigateToCustomerManager(manager.id)}
        />
      ))}
    </>
  )
}
